// Телефонная оболочка целиком (MOBILE_CONTRACT.md).
//
// Это не адаптив десктопа, а отдельная оболочка над теми же данными: одна
// колонка, нижний таб-бар, переходы стеком. Здесь живёт только обвязка —
// шапка, роутинг по стеку и таб-бар. Содержимое экранов пишут свои ветки,
// оболочка их монтирует и ничего о них не знает, кроме того, что они умеют
// попросить открыть заметку или беседу.

import { useEffect } from 'react'
import type { ReactNode } from 'react'
import type { SessionState, TreeNode } from '../protocol'
import { useAppStore } from '../store/appStore'
import Toast from '../shell/Toast'
import BaseScreen from './BaseScreen'
import ChatScreen from './ChatScreen'
import ChatsScreen from './ChatsScreen'
import FeedScreen from './FeedScreen'
import InstallBanner from './InstallBanner'
import MessengersScreen from './MessengersScreen'
import NoteScreen from './NoteScreen'
import OrchestratorScreen, { OrchestratorButton, useOrchestratorSub } from './OrchestratorScreen'
import Screen from './Screen'
import SearchScreen from './SearchScreen'
import TabBar from './TabBar'
import { useMobileStore, watchHistoryBack } from './mobileStore'
import { noteTitle, plural } from './nav'
import './mobile.css'

/** Сколько всего заметок в базе — для строки под заголовком корневого экрана. */
function countNotes(nodes: TreeNode[]): number {
  let total = 0
  for (const node of nodes) {
    if (node.kind === 'note') total += 1
    else total += countNotes(node.children ?? [])
  }
  return total
}

const CHAT_STATE: Record<SessionState, string> = {
  running: 'агент работает',
  idle: 'агент ждёт',
  waiting_permission: 'ждёт разрешения',
  exited: 'беседа завершена',
}

export default function MobileApp() {
  const screen = useMobileStore((s) => s.screen)
  const tab = useMobileStore((s) => s.tab)
  const push = useMobileStore((s) => s.push)

  const tree = useAppStore((s) => s.tree)
  const sessions = useAppStore((s) => s.sessions)
  const unread = useAppStore((s) => s.unread)
  const profile = useAppStore((s) => s.profile)

  // Системная кнопка «назад» на Android обязана возвращать по стеку, а не
  // закрывать установленное приложение (M1.4). Подписка идемпотентна и живёт
  // со страницей — отписываться на размонтировании оболочки нечему.
  useEffect(() => {
    watchHistoryBack()
  }, [])

  const openNote = (path: string) => push({ kind: 'note', path })
  const openChat = (sessionId: string) => push({ kind: 'chat', sessionId })
  const openOrchestrator = () => push({ kind: 'orchestrator' })
  const [orchSub, orchLive] = useOrchestratorSub()
  // «‹» в шапке не трогает стек напрямую, а откатывает history: снять экран
  // должен ровно один обработчик — тот же popstate, что ловит системное
  // «назад». Иначе стек и history разъезжаются, и системная кнопка начинает
  // проматывать по два экрана за нажатие.
  const back = () => history.back()

  let title = ''
  let sub = ''
  let body = null
  let onBack: (() => void) | undefined
  let actions: ReactNode = null

  if (screen.kind === 'orchestrator') {
    title = 'Оркестратор'
    sub = orchSub
    onBack = back
    body = <OrchestratorScreen onOpenChat={openChat} />
  } else if (screen.kind === 'note') {
    title = noteTitle(screen.path)
    sub = screen.path
    onBack = back
    body = <NoteScreen path={screen.path} onOpen={openNote} />
  } else if (screen.kind === 'chat') {
    const session = sessions.find((s) => s.id === screen.sessionId)
    title = session?.title || 'беседа'
    sub = session ? CHAT_STATE[session.state] : 'сессия не найдена'
    onBack = back
    body = <ChatScreen sessionId={screen.sessionId} />
  } else if (tab === 'feed') {
    title = 'Что изменилось'
    sub = unread > 0
      ? `${unread} ${plural(unread, 'непрочитанное', 'непрочитанных', 'непрочитанных')}`
      : 'всё прочитано'
    body = <FeedScreen onOpen={openNote} />
  } else if (tab === 'chats') {
    // Канал в счёт бесед не идёт: у него свой корень, и в списке бесед его
    // тоже нет — иначе «окон» было бы на одно больше, чем видно.
    const chats = sessions.filter((s) => s.role === 'chat').length
    title = 'Беседы'
    sub = `${chats} ${plural(chats, 'окно', 'окна', 'окон')} · в базу идёт только сохранённое`
    body = <ChatsScreen onOpen={openChat} />
  } else if (tab === 'messengers') {
    // Канал закреплён, и заголовок у раздела всегда один — меняется только
    // строка состояния под ним.
    const channel = sessions.find((s) => s.role === 'telegram')
    title = 'Телеграм'
    sub = channel ? `${CHAT_STATE[channel.state]} · канал держит аккаунт один` : 'канал не поднят'
    body = <MessengersScreen />
  } else if (tab === 'search') {
    const notes = countNotes(tree)
    title = 'Поиск'
    sub = `${notes} ${plural(notes, 'заметка', 'заметки', 'заметок')} в базе`
    body = <SearchScreen onOpen={openNote} />
  } else {
    const notes = countNotes(tree)
    title = 'agentbox'
    sub = `~/${profile ?? 'база'} · ${notes} ${plural(notes, 'заметка', 'заметки', 'заметок')}`
    body = <BaseScreen onOpen={openNote} />
  }

  // Вход в голосового оркестратора — кнопка с микрофоном в шапке «Бесед»;
  // пока разговор идёт, она видна на всех корневых экранах, чтобы вернуться.
  if (screen.kind === 'tab' && (tab === 'chats' || orchLive)) {
    actions = <OrchestratorButton onOpen={openOrchestrator} />
  }

  return (
    <div className="ab-mobile">
      <Screen title={title} sub={sub} onBack={onBack} actions={actions}>
        {body}
      </Screen>
      {/* «Поставить на экран Домой» (M10.2) — полосой над таб-баром, а не
          карточкой внутри экрана: предложение относится ко всему приложению
          и не должно уезжать вместе с прокруткой базы. */}
      <InstallBanner />
      <TabBar />
      {/* Потеря сети, новая версия и разовые сообщения. Статус-бара на телефоне
          нет, и это единственный канал таких плашек — общий с десктопом. */}
      <Toast />
    </div>
  )
}
